import * as React from 'react';
import { useForm } from 'react-hook-form';
import { LabelButton } from '@teamsnap/snap-ui';
import { Modal } from '@teamsnap/teamsnap-ui';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router';

// Local Imports
import api from 'core/api';
import * as ProgramSeasonsDivisions from 'core/gql/queries/program-season-divisions';
import * as GqlRegistrations from 'core/gql/queries/get-all-forms-reg';
import DuplicateOptions from './DuplicateOptions';
import DuplicateReg2FormStepOne from './DuplicateReg2FormStepOne';
import ChangesMadeWarningModal from './ChangesMadeWarningModal';
import DuplicateReg2ConfirmationModal from './DuplicateReg2ConfirmationModal';
import { SelectOption } from '@teamsnap/snap-ui/build/components/Select/Select.types';
import { sortByAlpha } from 'core/utils';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import {
  useDuplicateOptions,
  buildSettingsDefaultFormOptions,
  useFormHasAddOnFees,
  useFormHasRegistrationFees,
  useDuplicateRegForm,
  buildSettingsToDuplicate,
  buildNgbIntegrationData,
} from './utils';
import './DuplicateReg2FormModal.scss';

import { FeatureFlagService } from 'frontend-toolkit';
import { FeatureFlagConfig, FeatureFlags } from 'core/feature-flags';
import ENVIRONMENT from 'core/environment';
import { PartnerOffering } from '../types';

interface Props {
  onClose: (completed?: boolean) => void;
  formId: string;
  orgId: number;
}

export const DuplicateReg2FormModal = ({ onClose, formId, orgId }: Props) => {
  // NOTE: Feature flag for Auto Rostering needs to be removed once the feature is fully released
  const autoRosteringIsEnabled = FeatureFlagService.isFeatureEnabledForOrg(
    FeatureFlagConfig[FeatureFlags.AUTO_ROSTERING],
    ENVIRONMENT,
    orgId
  );

  const history = useHistory();

  const [currentStep, setCurrentStep] = React.useState<number>(0);
  const [showChangesMadeModal, setShowChangesMadeModal] =
    React.useState<boolean>(false);
  const [duplicateReg2ConfirmationModal, setDuplicateReg2ConfirmationModal] =
    React.useState<boolean>(false);
  const [savedDuplicateFormId, setSavedDuplicateFormId] = React.useState<
    number | null
  >(null);
  const [availableAddons, setAvailableAddons] = React.useState<PartnerOffering[]>([]);

  const allForms =
    useQuery(GqlRegistrations.QUERY, { variables: { organizationId: orgId } })
      ?.data?.forms || [];
  const selectedForm = allForms.find((form) => form.id === formId);

  const otherSettingsOptionsDefaults = buildSettingsDefaultFormOptions();

  const [formData, setFormData] = React.useState<
    Partial<DuplicateReg2FormModalInputs>
  >({
    name: selectedForm?.name + ' (Copy)' || '',
    program: selectedForm?.season?.program?.id || '',
    season: '',
    enabled: undefined,
  });

  const form = useForm<DuplicateReg2FormModalInputs>({
    mode: 'onBlur',
    defaultValues: {
      name: formData.name,
      program: formData.program,
      season: formData.season,
      enabled: undefined,
      season_dates: undefined,
    },
  });

  const { handleSubmit, watch, reset, getValues } = form;
  const [duplicateForm, { loading }] = useDuplicateRegForm({ orgId });

  const hasRegistrationFees = useFormHasRegistrationFees({ formId, orgId });
  const hasAddOnFees = useFormHasAddOnFees({ formId });
  const duplicateOptions = useDuplicateOptions({
    form,
    hasAddOnFees,
    hasRegistrationFees,
  });

  const programId = watch('program');
  const name = watch('name');
  const seasonId = watch('season');

  const programsList =
    useQuery(ProgramSeasonsDivisions.QUERY, {
      variables: { organizationId: orgId },
    })?.data?.programs || [];

  const programOptions: SelectOption[] =
    programsList
      .map((program) => ({
        label: program.name,
        value: program.id,
      }))
      .sort((a, b) => sortByAlpha(a.label, b.label)) || [];

  const getProgramSeasons = () => {
    try {
      const selectedProgram = programsList.find((p) => p.id === +programId);

      // Removed Archived seasons and seasons that has Reg1 forms created
      const seasonsNotArchived =
        selectedProgram?.seasons?.filter(({ isArchived }) => !isArchived) || [];
      return seasonsNotArchived.filter((s) => {
        const seasonHasReg1Forms = (form, season) =>
          !form.isReg2 && form.season.id === season.id;
        return !allForms.some((f) => seasonHasReg1Forms(f, s));
      });
    } catch (e) {
      console.error(`registration::getProgramSeasons(): ${e}`);
    }
  };

  React.useEffect(() => {
    let executed = false;

    if (!executed && orgId && programId) {
      executed = true;
    }
  }, [orgId, programId]);


  React.useEffect(() => {
    try {
      api.get<any>(`/organizations/${orgId}/offerings`).then((res) => {
        const offerings = res.data.offerings ?? [];
        setAvailableAddons(offerings);
      });
    } catch {
      setAvailableAddons([]);
    }
  }, [orgId]);

  const seasonOptions: SelectOption[] =
    (
      getProgramSeasons()?.map((season) => ({
        label: season.name,
        value: season.id,
      })) as any
    ).sort((a, b) => sortByAlpha(a.label, b.label)) || [];

  React.useEffect(() => {
    // Reset specific values when the program value changes.
    reset({
      ...formData,
      name: name,
      program: programId,
      season: '',
      enabled: undefined,
      season_dates: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  const handlePreviousAction = () => {
    setFormData({
      ...otherSettingsOptionsDefaults,
      ...formData,
      ...getValues(),
    });

    if (currentStep - 1 >= 0) {
      setCurrentStep(currentStep - 1);
    }

    return false;
  };

  const handleClose = () => {
    setShowChangesMadeModal(true);
  };

  const closeWarning = () => {
    setShowChangesMadeModal(false);
  };

  const submit = async (values) => {
    const updatedValues = {
      ...otherSettingsOptionsDefaults,
      ...formData,
      ...values,
      // NOTE: Only reset the ngbIntegrationCode when we are on the first step and the value is empty
      ...(currentStep === 0 &&
        !values.ngbIntegrationCode && {
          ngbIntegrationCode: null,
        }),
    };

    /*
      Note: This reset is prone to bugs, each step should have its own form and submit 
      to persist its data into a single formData object.
    */
    // Reset is used to update the default values of the form when we toggle between steps.
    reset(updatedValues);
    setFormData(updatedValues);

    if (currentStep === 0) {
      setCurrentStep(currentStep + 1);
    } else {
      try {
        const ngbIntegrationData = updatedValues.ngbIntegrationCode
          ? buildNgbIntegrationData(
              updatedValues.ngbIntegrationCode,
              updatedValues
            )
          : null;

        const availableOfferings = availableAddons.map((offering) => ({ id: offering.id }));

        const response = await duplicateForm({
          copyFormName: updatedValues.name,
          formId: +formId,
          settingsToDuplicate: buildSettingsToDuplicate(updatedValues),
          toScopeId: +updatedValues.season,
          organizationId: +orgId,
          toScopeType: 'season',
          data: {
            ...ngbIntegrationData,
            availableOfferings,
          }
        });
        if (response.data) {
          const { duplicateReg2Form } = response.data;
          setSavedDuplicateFormId(duplicateReg2Form.id);
          // Show confirmation modal
          setDuplicateReg2ConfirmationModal(true);
        }
      } catch (e) {
        console.error(e);
        reactiveFeedbackMessage({
          message: 'Unable to duplicate registration form.',
          autoclose: true,
          isError: true,
        });
      }
    }
  };

  return (
    <>
      <Modal
        heading={
          <span className="sui-heading-sm">Duplicate Registration Form</span>
        }
        show={!!formId}
        showClose
        closeFn={handleClose}
        testId="duplicate-registration-2-form--modal"
        style={{
          padding: 20,
          borderRadius: 4,
          width: 770,
          height: '90vh',
          maxHeight: 735,
        }}
      >
        <div className="sui-flex sui-flex-col sui-h-full">
          <div className="sui-flex sui-flex-1 [overflow:hidden]">
            <div className="sui-basis-3/12">
              <ul className="sui-list-none sui-text-desktop-4">
                <li
                  className={`sui-cursor-pointer sui-leading-none sui-mb-2 program-guide-item ${
                    currentStep === 0
                      ? 'program-guide-item--active'
                      : 'program-guide-item--complete'
                  }`}
                >
                  <span
                    className={`${
                      currentStep === 0 ? 'sui-text-blue-40' : null
                    }`}
                    onClick={() => {
                      setFormData({
                        ...otherSettingsOptionsDefaults,
                        ...formData,
                        ...getValues(),
                      });
                      setCurrentStep(0);
                    }}
                    onKeyDown={() => {
                      setFormData({
                        ...otherSettingsOptionsDefaults,
                        ...formData,
                        ...getValues(),
                      });
                      setCurrentStep(0);
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    General
                  </span>
                </li>
                <li className="sui-cursor-pointer sui-leading-none program-guide-item program-guide-item--active">
                  <span
                    className={`${
                      currentStep === 1
                        ? 'sui-text-blue-40'
                        : 'sui-text-gray-60'
                    }`}
                    onClick={() => {
                      if (seasonOptions.length === 0) return;
                      if (currentStep === 0) handleSubmit(submit)();
                    }}
                    onKeyDown={() => {
                      if (seasonOptions.length === 0) return;
                      if (currentStep === 0) handleSubmit(submit)();
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    Other settings
                  </span>
                </li>
              </ul>
            </div>

            <div className="sui-basis-9/12 [overflow-y:auto]">
              <div className="sui-bg-gray-98 sui-p-2">
                {currentStep === 0 && (
                  <DuplicateReg2FormStepOne
                    reactHookForm={form}
                    formData={formData}
                    seasonOptions={seasonOptions}
                    programOptions={programOptions}
                    seasonDifferentFromOriginal={
                      selectedForm?.season?.id !== seasonId
                    }
                  />
                )}

                {currentStep === 1 && (
                  <DuplicateOptions
                    form={form}
                    options={duplicateOptions}
                    submitting={loading}
                    autoRosteringIsEnabled={autoRosteringIsEnabled}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="sui-mt-2">
            <div className="sui-flex sui-justify-end">
              {currentStep > 0 ? (
                <LabelButton
                  disabled={loading}
                  labelText="Previous"
                  variantType="secondary"
                  className="sui-px-3 sui-mr-1 sui-cursor-pointer"
                  data-testid={'DuplicateReg2FormoModal--previous'}
                  onClick={handlePreviousAction}
                />
              ) : null}

              <LabelButton
                disabled={seasonOptions.length === 0 || loading}
                labelText={`${currentStep > 0 ? 'Create' : 'Next'}`}
                variantType="primary"
                className="sui-px-3 sui-cursor-pointer"
                data-testid={'DuplicateReg2FormoModal--next'}
                onClick={() => handleSubmit(submit)()}
              />
            </div>
          </div>
        </div>
      </Modal>

      {showChangesMadeModal && (
        <ChangesMadeWarningModal
          isOpen={showChangesMadeModal}
          closeWarning={closeWarning}
          closeFn={() => {
            setShowChangesMadeModal(false);
            onClose();
          }}
        />
      )}

      {duplicateReg2ConfirmationModal && (
        <DuplicateReg2ConfirmationModal
          isOpen={duplicateReg2ConfirmationModal}
          onClose={() => setDuplicateReg2ConfirmationModal(false)}
          onComplete={() => {
            setSavedDuplicateFormId(null);
            setDuplicateReg2ConfirmationModal(false);
            onClose(true);
            history.push(
              `/organizations/${orgId}/registration/${savedDuplicateFormId}/3`,
              {
                prevPath: history.location.pathname,
              }
            );
          }}
        />
      )}
    </>
  );
};

export type DuplicateReg2FormModalInputs = {
  name: string;
  program: string;
  season: string;
  ngbIntegrationId: string;
  ngbIntegrationCode: string;
  enabled: boolean | undefined;
  season_dates: string;
};
